import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageShell from "@/components/PageShell";
import { toast } from "sonner";
import { Loader2, MapPin, Briefcase, Building2, X, CheckCircle2, Search } from "lucide-react";

const TYPES = ["All", "Internship", "Freelance", "Entry-level"];

export default function Jobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  const load = () => api.get("/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  useEffect(() => { load(); }, []);

  const filtered = (jobs || []).filter((j) =>
    (filter === "All" || j.job_type === filter) &&
    (j.title.toLowerCase().includes(query.toLowerCase()) || j.company.toLowerCase().includes(query.toLowerCase()) || j.category.toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <PageShell>
      <div>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A222B]">AI Job Board</span>
        <h1 className="font-display mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2825]" style={{ fontWeight: 700 }}>Curated AI opportunities</h1>
        <p className="mt-3 text-[#6E665E] max-w-2xl">Every role is reviewed by our team — real AI work, no misleading labels.</p>
      </div>

      {!user?.profile_complete && (
        <div className="mt-6 flex items-center justify-between gap-4 bg-[#FDF0EC] border border-[#F8D7C4] rounded-2xl px-6 py-4">
          <p className="text-sm text-[#2D2825]">Complete your profile to unlock one-click applications.</p>
          <Link to="/profile" className="shrink-0 text-sm font-semibold text-[#7A222B] hover:underline">Complete now →</Link>
        </div>
      )}

      <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {TYPES.map((t) => (
            <button key={t} onClick={() => setFilter(t)} data-testid={`job-filter-${t.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === t ? "bg-[#7A222B] text-white" : "bg-white border border-[#EAE3DB] text-[#6E665E] hover:border-[#D99B9F]"}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-[#948A80] absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="job-search" placeholder="Search roles…"
            className="pl-10 pr-4 py-2.5 rounded-full border border-[#EAE3DB] bg-white text-sm w-full sm:w-64 focus:outline-none focus:border-[#7A222B] transition" />
        </div>
      </div>

      {jobs === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7A222B]" /></div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 bg-white border border-[#EAE3DB] rounded-2xl p-12 text-center text-[#6E665E]" data-testid="no-jobs">No roles match your filters right now.</div>
      ) : (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filtered.map((j) => (
            <div key={j.id} data-testid={`job-card-${j.id}`} className="bg-white border border-[#EAE3DB] rounded-3xl p-7 hover:shadow-xl hover:shadow-[#2D2825]/5 transition-shadow flex flex-col">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F5EFE6] text-[#7A222B]">{j.job_type}</span>
                  <h3 className="mt-3 text-lg font-semibold text-[#2D2825]">{j.title}</h3>
                  <p className="text-sm text-[#6E665E] flex items-center gap-1.5 mt-1"><Building2 className="w-4 h-4" /> {j.company}</p>
                </div>
                {j.has_applied && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#2E7D32]"><CheckCircle2 className="w-4 h-4" /> Applied</span>}
              </div>
              <p className="mt-4 text-sm text-[#6E665E] leading-relaxed line-clamp-2">{j.description}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {j.skills?.slice(0, 4).map((s) => <span key={s} className="text-xs bg-[#FDF0EC] text-[#7A222B] px-2.5 py-1 rounded-full">{s}</span>)}
              </div>
              <div className="mt-5 pt-5 border-t border-[#EAE3DB] flex items-center justify-between">
                <div className="flex items-center gap-4 text-xs text-[#948A80]">
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.location}</span>
                  <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {j.work_mode}</span>
                  {j.stipend && <span className="font-medium text-[#2D2825]">{j.stipend}</span>}
                </div>
                <button onClick={() => setActive(j)} disabled={j.has_applied} data-testid={`job-view-${j.id}`}
                  className="text-sm font-semibold text-[#7A222B] hover:text-[#5C1D24] transition-colors disabled:text-[#948A80]">
                  {j.has_applied ? "Applied" : "View & apply →"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {active && <ApplyModal job={active} canApply={user?.profile_complete} onClose={() => setActive(null)} onApplied={() => { setActive(null); load(); }} />}
    </PageShell>
  );
}

function ApplyModal({ job, canApply, onClose, onApplied }) {
  const [cover, setCover] = useState("");
  const [resume, setResume] = useState("");
  const [loading, setLoading] = useState(false);

  const apply = async () => {
    setLoading(true);
    try {
      await api.post(`/jobs/${job.id}/apply`, { cover_note: cover, resume_link: resume });
      toast.success("Application submitted!");
      onApplied();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not apply");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#2D2825]/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#FDFBF7] rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-7 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="apply-modal">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F5EFE6] text-[#7A222B]">{job.job_type}</span>
            <h3 className="mt-3 font-display text-2xl font-bold text-[#2D2825]" style={{ fontWeight: 700 }}>{job.title}</h3>
            <p className="text-sm text-[#6E665E]">{job.company} · {job.location} · {job.work_mode}</p>
          </div>
          <button onClick={onClose} data-testid="apply-modal-close" className="text-[#948A80] hover:text-[#2D2825] transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <p className="mt-5 text-sm text-[#6E665E] leading-relaxed">{job.description}</p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {job.skills?.map((s) => <span key={s} className="text-xs bg-[#FDF0EC] text-[#7A222B] px-2.5 py-1 rounded-full">{s}</span>)}
        </div>

        {!canApply ? (
          <div className="mt-6 bg-[#FDF0EC] border border-[#F8D7C4] rounded-2xl p-5 text-center">
            <p className="text-sm text-[#2D2825]">Complete your profile (headline, college, 3+ skills) to apply.</p>
            <Link to="/profile" className="mt-3 inline-block text-sm font-semibold text-[#7A222B] hover:underline">Go to profile →</Link>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-[#2D2825]">Cover note (optional)</label>
              <textarea rows={3} value={cover} onChange={(e) => setCover(e.target.value)} data-testid="apply-cover-note"
                className="mt-1.5 w-full rounded-xl border border-[#EAE3DB] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#7A222B] transition" placeholder="Why you're a fit…" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D2825]">Resume link (optional)</label>
              <input value={resume} onChange={(e) => setResume(e.target.value)} data-testid="apply-resume-link"
                className="mt-1.5 w-full rounded-xl border border-[#EAE3DB] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#7A222B] transition" placeholder="Defaults to your profile resume" />
            </div>
            <button onClick={apply} disabled={loading} data-testid="apply-submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#7A222B] hover:bg-[#5C1D24] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Submit application
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
